import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { get as lodashGet } from 'lodash';
import queryString from 'query-string';

import { formatBytes, formatDownloadFilenameNoExt } from 'helpers/strings';
import QUERY_VIDEO_DOWNLOAD_INFO from './gql/queryVideoDownloadInfo.gql';

const getIdFromData = (data) => lodashGet(data, 'node.databaseId', 0) || 0;

const getCreatedAtFromData = (data) =>
  lodashGet(data, 'node.createdAt', Date.now());

const getRenditionFromData = (data) => {
  const staticRenditions = lodashGet(data, 'node.staticRenditions', []) || [];
  return (
    staticRenditions.find((r) => r.quality === 'HIGH') ||
    staticRenditions.find((r) => r.quality === 'MEDIUM') ||
    staticRenditions.find((r) => r.quality === 'LOW') ||
    null
  );
};

const getProjectNameFromData = (data) =>
  lodashGet(data, 'node.project.name', '') || '';

const getTagValuesFromData = (data) => {
  const tagEdges = lodashGet(data, 'node.tags.edges', []) || [];
  return tagEdges.map((edge) => edge.node.displayValue);
};

export default function useVideoDownloadInfo({ videoNodeId }) {
  const { data } = useQuery(QUERY_VIDEO_DOWNLOAD_INFO, {
    variables: { id: videoNodeId },
    fetchPolicy: 'cache-and-network',
  });

  const rendition = useMemo(() => getRenditionFromData(data), [data]);

  const [ready, sizeString, downloadUrl] = useMemo(
    () => {
      if (!rendition || typeof rendition.url !== 'string') {
        return [false, '', ''];
      }

      const filenameNoExt = formatDownloadFilenameNoExt({
        projectName: getProjectNameFromData(data),
        tagValues: getTagValuesFromData(data),
        date: new Date(getCreatedAtFromData(data)),
        identifier: getIdFromData(data),
      });
      const filename = `${filenameNoExt}.mp4`;

      const { url, query } = queryString.parseUrl(rendition.url);
      const fixedUrl = queryString.stringifyUrl({
        url,
        query: { ...query, download: filename },
      });

      return [true, formatBytes(rendition.filesize), fixedUrl];
    },
    [data, rendition],
  );

  return [ready, sizeString, downloadUrl];
}
